// src/components/ProjectManagement/ClientProjectsView.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';

const ClientProjectsView = ({ client, onBack, currentUser }) => {
  const [activeProjects, setActiveProjects] = useState([]);
  const [completedProjects, setCompletedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProjectId, setExpandedProjectId] = useState(null);

  // OPTIMIZED: Fetch everything in PARALLEL, not sequentially
  useEffect(() => {
    if (client?.id) {
      fetchClientProjectsOptimized();
    }
  }, [client]);

  const fetchClientProjectsOptimized = async () => {
    setLoading(true);
    try {
      // Step 1: Fetch all projects for this client (1 query)
      const projectsQuery = query(
        collection(db, 'projects'),
        where('clientId', '==', client.id)
      );
      const projectsSnapshot = await getDocs(projectsQuery);
      const projects = projectsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        endDateObj: doc.data().endDate?.toDate?.() || (doc.data().endDate ? new Date(doc.data().endDate) : null),
        startDateObj: doc.data().startDate?.toDate?.() || (doc.data().startDate ? new Date(doc.data().startDate) : null)
      }));

      if (projects.length === 0) {
        setActiveProjects([]);
        setCompletedProjects([]);
        setLoading(false);
        return;
      }

      // Step 2: Get ALL tasks for ALL projects in ONE query using "in" operator
      const projectIds = projects.map(p => p.id);
      
      // Firestore "in" query can only handle up to 30 values
      // If more than 30 projects, we need to batch
      let allTasks = [];
      
      if (projectIds.length <= 30) {
        // Single query for all projects
        const tasksQuery = query(
          collection(db, 'tasks'),
          where('projectId', 'in', projectIds)
        );
        const tasksSnapshot = await getDocs(tasksQuery);
        allTasks = tasksSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          dueDateObj: doc.data().endDate?.toDate?.() || (doc.data().endDate ? new Date(doc.data().endDate) : null),
          startDateObj: doc.data().startDate?.toDate?.() || (doc.data().startDate ? new Date(doc.data().startDate) : null),
          completedAtObj: doc.data().completedAt?.toDate?.() || null
        }));
      } else {
        // Batch queries for many projects
        const batchSize = 30;
        const batches = [];
        for (let i = 0; i < projectIds.length; i += batchSize) {
          batches.push(projectIds.slice(i, i + batchSize));
        }
        
        const batchPromises = batches.map(batchIds => 
          getDocs(query(collection(db, 'tasks'), where('projectId', 'in', batchIds)))
        );
        
        const batchResults = await Promise.all(batchPromises);
        allTasks = batchResults.flatMap(snapshot => 
          snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            dueDateObj: doc.data().endDate?.toDate?.() || (doc.data().endDate ? new Date(doc.data().endDate) : null),
            startDateObj: doc.data().startDate?.toDate?.() || (doc.data().startDate ? new Date(doc.data().startDate) : null),
            completedAtObj: doc.data().completedAt?.toDate?.() || null
          }))
        );
      }

      // Step 3: Group tasks by projectId using Map (O(n) instead of O(n²))
      const tasksByProject = new Map();
      allTasks.forEach(task => {
        if (!tasksByProject.has(task.projectId)) {
          tasksByProject.set(task.projectId, []);
        }
        tasksByProject.get(task.projectId).push(task);
      });

      // Step 4: Build project data with their tasks
      const projectsData = projects.map(project => {
        const projectTasks = tasksByProject.get(project.id) || [];
        const completedTasks = projectTasks.filter(t => t.status === 'completed' && t.qcVerified === true).length;
        const progress = projectTasks.length > 0 ? Math.round((completedTasks / projectTasks.length) * 100) : 0;
        
        const tasksWithProgress = projectTasks.map(task => ({
          ...task,
          taskProgress: task.status === 'completed' ? 100 : task.status === 'ongoing' ? 50 : 0
        }));
        
        return {
          ...project,
          tasks: tasksWithProgress,
          progress,
          completedTasks,
          totalTasks: projectTasks.length
        };
      });

      // Step 5: Separate and sort
      const active = projectsData.filter(p => p.progress < 100);
      const completed = projectsData.filter(p => p.progress === 100);
      
      completed.sort((a, b) => {
        const dateA = a.endDateObj || new Date(0);
        const dateB = b.endDateObj || new Date(0);
        return dateB - dateA;
      });
      
      active.sort((a, b) => b.progress - a.progress);
      
      setActiveProjects(active);
      setCompletedProjects(completed);
    } catch (error) {
      console.error('Error fetching client projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';
    if (date.toDate) return date.toDate().toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  const getProgressColor = (progress) => {
    if (progress === 100) return '#10b981';
    if (progress >= 75) return '#3b82f6';
    if (progress >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const getTaskStatusColor = (status) => {
    switch(status) {
      case 'completed': return '#10b981';
      case 'ongoing': return '#3b82f6';
      case 'pending': return '#f59e0b';
      default: return '#9ca3af';
    }
  };

  const getTaskStatusText = (status) => {
    switch(status) {
      case 'completed': return 'Completed';
      case 'ongoing': return 'In Progress';
      case 'pending': return 'Pending';
      default: return status;
    }
  };

  const toggleProjectExpand = (projectId) => {
    setExpandedProjectId(expandedProjectId === projectId ? null : projectId);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading client projects...</p>
      </div>
    );
  }

  return (
    <div className="client-projects-view" style={{ padding: '24px', minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header with Back Button */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px', 
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            padding: '10px 20px',
            borderRadius: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '500',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
        >
          ← Back to Clients
        </button>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ 
              width: '60px', 
              height: '60px', 
              borderRadius: '50%', 
              background: 'linear-gradient(135deg, #800020, #5a0016)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              color: 'white'
            }}>
              {client.name?.charAt(0).toUpperCase()}
            </span>
            {client.name}
          </h1>
          <p style={{ color: '#666', marginTop: '8px' }}>
            {client.email && <span>✉️ {client.email} &nbsp;|&nbsp;</span>}
            {client.phone && <span>📞 {client.phone} &nbsp;|&nbsp;</span>}
            {client.location && <span>📍 {client.location}</span>}
          </p>
        </div>
      </div>

      {/* Stats Summary Cards */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '32px',
        flexWrap: 'wrap'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #800020, #5a0016)',
          borderRadius: '16px',
          padding: '20px 32px',
          color: 'white',
          textAlign: 'center',
          minWidth: '140px'
        }}>
          <div style={{ fontSize: '32px', fontWeight: '800' }}>{activeProjects.length + completedProjects.length}</div>
          <div style={{ opacity: 0.9, fontSize: '13px' }}>Total Projects</div>
        </div>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px 32px',
          border: '1px solid #e5e7eb',
          textAlign: 'center',
          minWidth: '140px'
        }}>
          <div style={{ fontSize: '32px', fontWeight: '800', color: '#3b82f6' }}>{activeProjects.length}</div>
          <div style={{ color: '#666', fontSize: '13px' }}>Active</div>
        </div>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px 32px',
          border: '1px solid #e5e7eb',
          textAlign: 'center',
          minWidth: '140px'
        }}>
          <div style={{ fontSize: '32px', fontWeight: '800', color: '#10b981' }}>{completedProjects.length}</div>
          <div style={{ color: '#666', fontSize: '13px' }}>Completed</div>
        </div>
      </div>

      {/* Two-Column Layout */}
      <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
        {/* LEFT COLUMN - Active Projects */}
        <div style={{ flex: 2, minWidth: '300px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            🚀 Active Projects
            <span style={{ fontSize: '13px', background: '#dbeafe', padding: '4px 12px', borderRadius: '20px', fontWeight: '400', color: '#1e40af' }}>
              {activeProjects.length} projects
            </span>
          </h2>
          
          {activeProjects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', background: 'white', borderRadius: '16px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
              <p style={{ color: '#666' }}>No active projects</p>
            </div>
          ) : (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'row', 
              gap: '20px',
              overflowX: 'auto',
              paddingBottom: '16px'
            }}>
              {activeProjects.map(project => (
                <div key={project.id} style={{
                  background: 'white',
                  borderRadius: '16px',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  minWidth: '320px',
                  maxWidth: '320px',
                  flexShrink: 0
                }}>
                  <div 
                    onClick={() => toggleProjectExpand(project.id)}
                    style={{
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (expandedProjectId !== project.id) {
                        e.currentTarget.style.background = '#fafafa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                    }}
                  >
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: '700', margin: 0, flex: 1 }}>{project.projectName}</h3>
                        <span style={{
                          background: getProgressColor(project.progress),
                          color: 'white',
                          padding: '3px 10px',
                          borderRadius: '20px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>
                          {project.progress}%
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '12px', fontSize: '10px', color: '#999', flexWrap: 'wrap' }}>
                        <span>📅 {formatDate(project.startDateObj)}</span>
                        <span>→ {formatDate(project.endDateObj)}</span>
                        <span>📋 {project.completedTasks}/{project.totalTasks}</span>
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{ 
                          width: `${project.progress}%`, 
                          height: '100%', 
                          background: getProgressColor(project.progress), 
                          transition: 'width 0.3s ease' 
                        }} />
                      </div>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '10px',
                      color: '#999',
                      paddingTop: '8px',
                      borderTop: '1px solid #f0f0f0'
                    }}>
                      <span>{expandedProjectId === project.id ? '▲ Hide Tasks' : '▼ View Tasks'}</span>
                    </div>
                  </div>

                  {expandedProjectId === project.id && (
                    <div style={{ 
                      padding: '16px', 
                      background: '#f9fafb', 
                      borderTop: '1px solid #e5e7eb',
                      animation: 'slideDown 0.2s ease'
                    }}>
                      <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                        📋 Tasks ({project.tasks.length})
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '300px', overflowY: 'auto' }}>
                        {project.tasks.map(task => (
                          <div key={task.id} style={{
                            background: 'white',
                            borderRadius: '10px',
                            padding: '10px 12px',
                            borderLeft: `3px solid ${getTaskStatusColor(task.status)}`,
                            fontSize: '12px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
                              <span style={{ fontWeight: '600' }}>{task.title}</span>
                              <span style={{
                                background: getTaskStatusColor(task.status),
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '9px',
                                fontWeight: '600'
                              }}>
                                {getTaskStatusText(task.status)}
                              </span>
                            </div>
                            <div style={{ marginTop: '6px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#9ca3af', marginBottom: '3px' }}>
                                <span>Progress</span>
                                <span>{task.taskProgress}%</span>
                              </div>
                              <div style={{ height: '3px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                                <div style={{ 
                                  width: `${task.taskProgress}%`, 
                                  height: '100%', 
                                  background: getTaskStatusColor(task.status), 
                                  transition: 'width 0.3s ease' 
                                }} />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '9px', color: '#9ca3af', flexWrap: 'wrap' }}>
                              {task.assignedToNames && (
                                <span>👥 {Array.isArray(task.assignedToNames) ? task.assignedToNames.join(', ') : task.assignedToNames}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT COLUMN - Completed Projects */}
        <div style={{ flex: 1, minWidth: '280px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            ✅ Completed Projects
            <span style={{ fontSize: '13px', background: '#d1fae5', padding: '4px 12px', borderRadius: '20px', fontWeight: '400', color: '#065f46' }}>
              {completedProjects.length} projects
            </span>
          </h2>
          
          {completedProjects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '16px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
              <p style={{ color: '#666', fontSize: '13px' }}>No completed projects yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {completedProjects.map(project => (
                <div key={project.id} style={{
                  background: 'white',
                  borderRadius: '14px',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <div style={{ 
                    background: 'linear-gradient(135deg, #10b981, #059669)', 
                    padding: '12px 16px',
                    color: 'white'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', opacity: 0.9 }}>Completed</span>
                      <span>✅</span>
                    </div>
                    <h4 style={{ fontSize: '14px', fontWeight: '700', margin: '8px 0 4px 0' }}>{project.projectName}</h4>
                  </div>
                  <div style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11px' }}>
                      <span style={{ color: '#666' }}>Tasks</span>
                      <span style={{ fontWeight: '600' }}>{project.totalTasks} total</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11px' }}>
                      <span style={{ color: '#666' }}>Completed</span>
                      <span style={{ fontWeight: '600', color: '#10b981' }}>{formatDate(project.endDateObj)}</span>
                    </div>
                    {project.description && (
                      <p style={{ fontSize: '11px', color: '#999', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                        {project.description.substring(0, 80)}{project.description.length > 80 ? '...' : ''}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default ClientProjectsView;